/* Canvello v8 — quick view (Task 4.1). sections/cv-quick-view.liquid.
 *
 * A card's "Quick view" is a link to the product (snippets/cv-card.liquid),
 * so it works without JS. With JS, a click (or Enter / Space) fetches
 * /products/<handle>?section_id=cv-quick-view and shows it in one shared
 * modal <dialog>:
 *   - the page behind is inert and its scroll locked; Tab wraps inside;
 *     Escape, the × and the scrim close it; focus goes to the × on opening
 *     and back to the card's button on closing;
 *   - the variant picker: one radio group per option; the chosen variant
 *     sets the form's id, price, SKU, stock line, image and the Full details
 *     link; values with no variant or none in stock, given the other
 *     choices, are struck through; a missing or sold-out combination
 *     disables Add to bag and says so;
 *   - Add to bag posts /cart/add.js in place, fires cart:updated with the
 *     cart (the header's bag follows) and announces it; a refusal is shown on
 *     the button and announced.
 * Responses are kept for the page's life. Delegated from document, so cards
 * swapped in by the collection's filters work too.
 */
(() => {
  if (window.CVQuickView) return;

  const root = document.documentElement;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';
  const visible = (el) => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  const cache = new Map();
  let dialog = null;
  let trigger = null;
  let pending = null;

  const say = (box, text) => {
    const live = box.querySelector('[data-cv-qv-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = text));
  };

  function ensure() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'cv-qv';
    document.body.appendChild(dialog);

    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
    dialog.addEventListener('close', () => {
      root.removeAttribute('data-cv-locked');
      dialog.replaceChildren();
      if (trigger && document.contains(trigger) && visible(trigger)) trigger.focus();
      trigger = null;
    });
    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-cv-qv-close]')) return close();
      if (e.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
      }
    });
    dialog.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(visible);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    });
    dialog.addEventListener('change', (e) => {
      if (e.target.matches('[data-cv-qv-opt] input')) update(dialog.querySelector('[data-cv-qv-box]'));
    });
    dialog.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-cv-qv-form]');
      if (!form || !window.fetch) return;
      e.preventDefault();
      add(form);
    });
    return dialog;
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
  }

  async function open(link) {
    const url = new URL(link.href, location.origin);
    const key = url.pathname;
    if (pending) pending.abort();
    const ctrl = (pending = new AbortController());
    link.setAttribute('aria-busy', 'true');
    let html = cache.get(key);
    try {
      if (!html) {
        url.searchParams.set('section_id', 'cv-quick-view');
        const r = await fetch(url.pathname + url.search, { signal: ctrl.signal, headers: { Accept: 'text/html' } });
        if (!r.ok) throw new Error(String(r.status));
        html = await r.text();
        cache.set(key, html);
      }
    } catch (err) {
      link.removeAttribute('aria-busy');
      if (err.name === 'AbortError') return;
      location.assign(link.href);
      return;
    }
    link.removeAttribute('aria-busy');
    if (ctrl !== pending) return;
    pending = null;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const box = doc.querySelector('[data-cv-qv-box]');
    if (!box) {
      location.assign(link.href);
      return;
    }
    const d = ensure();
    const title = box.querySelector('h2[id]');
    if (title) d.setAttribute('aria-labelledby', title.id);
    d.replaceChildren(document.adoptNode(box));
    trigger = link;
    d.showModal();
    root.setAttribute('data-cv-locked', '');
    d.scrollTop = 0;
    const x = d.querySelector('[data-cv-qv-close]');
    if (x) x.focus();
    const live = d.querySelector('[data-cv-qv-box]');
    update(live, true);
    if (window.CVMotion) window.CVMotion.scan(d);
  }

  function variants(box) {
    if (!box.cvVariants) {
      try {
        box.cvVariants = JSON.parse(box.querySelector('[data-cv-qv-variants]').textContent);
      } catch (e) {
        box.cvVariants = [];
      }
    }
    return box.cvVariants;
  }

  function update(box, first = false) {
    if (!box) return;
    const all = variants(box);
    const sets = Array.from(box.querySelectorAll('[data-cv-qv-opt]'));
    const chosen = sets.map((fs) => {
      const c = fs.querySelector('input:checked');
      return c ? c.value : null;
    });
    const variant = sets.length ? all.find((v) => v.options.every((o, i) => o === chosen[i])) : all[0];

    // Strike values that, with the other choices, match no variant in stock.
    sets.forEach((fs, i) => {
      fs.querySelectorAll('input').forEach((input) => {
        const test = chosen.slice();
        test[i] = input.value;
        const ok = all.some((v) => v.available && v.options.every((o, j) => o === test[j]));
        input.closest('label').toggleAttribute('data-off', !ok);
      });
    });

    const add = box.querySelector('[data-cv-qv-add]');
    const label = box.querySelector('[data-cv-qv-add-label]');
    const id = box.querySelector('[data-cv-qv-id]');
    const stock = box.querySelector('[data-cv-qv-stock]');
    const ships = box.dataset.ships;

    if (!variant) {
      add.disabled = true;
      label.textContent = box.dataset.unavailable;
      stock.textContent = box.dataset.unavailable;
      return;
    }
    id.value = variant.id;
    add.disabled = !variant.available;
    label.textContent = variant.available ? box.dataset.add : box.dataset.sold;
    if (!first) {
      const price = box.querySelector('[data-cv-qv-price]');
      price.textContent = variant.price;
      if (variant.was) {
        const s = document.createElement('s');
        s.className = 'cv-qv__was';
        s.textContent = variant.was;
        price.appendChild(s);
      }
      stock.textContent = variant.stock || (!variant.available ? box.dataset.sold : (variant.only || box.dataset.inStock) + ' · ' + ships);
    }
    const sku = box.querySelector('[data-cv-qv-sku]');
    const skuWrap = box.querySelector('[data-cv-qv-sku-wrap]');
    if (sku) sku.textContent = variant.sku || '';
    if (skuWrap) skuWrap.hidden = !variant.sku;
    const details = box.querySelector('[data-cv-qv-details]');
    if (details && variant.url) details.href = variant.url;
    if (variant.media) {
      const imgs = box.querySelectorAll('[data-cv-qv-media]');
      const has = Array.from(imgs).some((m) => m.dataset.cvQvMedia === String(variant.media));
      if (has) imgs.forEach((m) => (m.hidden = m.dataset.cvQvMedia !== String(variant.media)));
    }
  }

  function add(form) {
    const box = form.closest('[data-cv-qv-box]');
    const btn = form.querySelector('[data-cv-qv-add]');
    const label = form.querySelector('[data-cv-qv-add-label]');
    if (!btn || btn.getAttribute('aria-busy') === 'true' || btn.disabled) return;
    const text = label.textContent;
    btn.setAttribute('aria-busy', 'true');
    const settle = (msg, ms) => {
      label.textContent = msg;
      setTimeout(() => {
        if (!document.contains(btn)) return;
        label.textContent = text;
        btn.removeAttribute('aria-busy');
      }, ms);
    };
    const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
    fetch(form.action.replace(/\/?$/, '.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.description || body.message || box.dataset.error);
        return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      })
      .then((cart) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, item_count: cart.item_count } }));
        settle(box.dataset.added, 2200);
        say(box, box.dataset.added);
      })
      .catch((err) => {
        const msg = (err && err.message) || box.dataset.error;
        settle(box.dataset.errorShort, 3200);
        say(box, msg);
      });
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[data-cv-qv]');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    open(link);
  });
  // A link that acts as a button answers Space too.
  document.addEventListener('keydown', (e) => {
    if (e.key !== ' ' || !e.target.matches || !e.target.matches('a[data-cv-qv]')) return;
    e.preventDefault();
    open(e.target);
  });

  window.CVQuickView = { open, close };
})();
